import { useState } from 'react';
import type { CartItemResponse, SelectedAddOn } from '@/features/cart/types';

export interface UseCartItemEditReturn {
  isEditing: boolean;
  editGuestCount: number;
  editAddOnsPerGuest: SelectedAddOn[][];
  removedItems: Set<string>;
  removedGuests: Set<number>;
  startEdit: () => void;
  cancelEdit: () => void;
  setEditGuestCount: (count: number) => void;
  setEditAddOnsPerGuest: React.Dispatch<React.SetStateAction<SelectedAddOn[][]>>;
  removeGuest: (guestIndex: number) => void;
  removeAddOnFromGuest: (guestIndex: number, addOnId: string) => void;
  restoreGuest: (guestIndex: number) => void;
  restoreAddOnFromGuest: (guestIndex: number, addOnId: string) => void;
  isItemMarkedForRemoval: (guestIndex: number, addOnId: string) => boolean;
  getEditModeGuestsWithAddOn: (addOnId: string) => number[];
  getRemovedGuestsWithAddOn: (addOnId: string) => number[];
  resetEditState: () => void;
  removeAddOnCompletely: (addOnId: string) => void;
  restoreAddOnCompletely: (addOnId: string) => void;
  isAddOnCompletelyRemoved: (addOnId: string) => boolean;
  setAddOnQuantityForGuest: (guestIndex: number, addOnId: string, newQuantity: number) => void;
}

export function useCartItemEdit(cartItem: CartItemResponse): UseCartItemEditReturn {
  const [isEditing, setIsEditing] = useState(false);
  const [editGuestCount, setEditGuestCount] = useState(cartItem.total_guests);
  const [editAddOnsPerGuest, setEditAddOnsPerGuest] = useState<SelectedAddOn[][]>(cartItem.add_ons_per_guest);
  const [removedItems, setRemovedItems] = useState<Set<string>>(new Set());
  const [removedGuests, setRemovedGuests] = useState<Set<number>>(new Set());

  const startEdit = () => {
    // Deep copy the add_ons_per_guest array to ensure fresh state
    const freshAddOnsPerGuest = cartItem.add_ons_per_guest.map(guestAddOns =>
      guestAddOns ? guestAddOns.map(addOn => ({ ...addOn })) : []
    );

    setIsEditing(true);
    setEditGuestCount(cartItem.total_guests);
    setEditAddOnsPerGuest(freshAddOnsPerGuest);
    setRemovedItems(new Set());
    setRemovedGuests(new Set());
  };

  const cancelEdit = () => {
    // Deep copy the add_ons_per_guest array to ensure fresh state
    const freshAddOnsPerGuest = cartItem.add_ons_per_guest.map(guestAddOns =>
      guestAddOns ? guestAddOns.map(addOn => ({ ...addOn })) : []
    );

    setIsEditing(false);
    setEditGuestCount(cartItem.total_guests);
    setEditAddOnsPerGuest(freshAddOnsPerGuest);
    setRemovedItems(new Set());
    setRemovedGuests(new Set());
  };

  const removeGuest = (guestIndex: number) => {
    if (editGuestCount <= 1) return;
    setEditGuestCount(prev => prev - 1);
    // Mark all add-ons from this guest as removed
    const guestAddOns = editAddOnsPerGuest[guestIndex];
    const newRemovedItems = new Set(removedItems);
    const newRemovedGuests = new Set(removedGuests);
    newRemovedGuests.add(guestIndex);
    guestAddOns.forEach(addOn => {
      newRemovedItems.add(`${guestIndex}-${addOn.add_on_id}`);
    });
    setRemovedItems(newRemovedItems);
    setRemovedGuests(newRemovedGuests);
  };

  const removeAddOnFromGuest = (guestIndex: number, addOnId: string) => {
    const key = `${guestIndex}-${addOnId}`;
    const newRemovedItems = new Set(removedItems);
    newRemovedItems.add(key);
    setRemovedItems(newRemovedItems);
  };

  const restoreGuest = (guestIndex: number) => {
    const newRemovedGuests = new Set(removedGuests);
    newRemovedGuests.delete(guestIndex);
    setRemovedGuests(newRemovedGuests);

    // Restore all add-ons from this guest
    const guestAddOns = editAddOnsPerGuest[guestIndex];
    const newRemovedItems = new Set(removedItems);
    guestAddOns.forEach(addOn => {
      const key = `${guestIndex}-${addOn.add_on_id}`;
      newRemovedItems.delete(key);
    });
    setRemovedItems(newRemovedItems);

    // Restore guest count
    setEditGuestCount(prev => prev + 1);
  };

  const restoreAddOnFromGuest = (guestIndex: number, addOnId: string) => {
    const key = `${guestIndex}-${addOnId}`;
    const newRemovedItems = new Set(removedItems);
    newRemovedItems.delete(key);
    setRemovedItems(newRemovedItems);
  };

  const isItemMarkedForRemoval = (guestIndex: number, addOnId: string): boolean => {
    const key = `${guestIndex}-${addOnId}`;
    return removedItems.has(key);
  };

  const getEditModeGuestsWithAddOn = (addOnId: string): number[] => {
    const guests: number[] = [];
    editAddOnsPerGuest.forEach((guestAddOns, index) => {
      if (guestAddOns.some(addOn => addOn.add_on_id === addOnId) && !isItemMarkedForRemoval(index, addOnId) && !removedGuests.has(index)) {
        guests.push(index + 1);
      }
    });
    return guests;
  };

  const getRemovedGuestsWithAddOn = (addOnId: string): number[] => {
    const guests: number[] = [];
    editAddOnsPerGuest.forEach((guestAddOns, index) => {
      if (removedGuests.has(index) && guestAddOns.some(addOn => addOn.add_on_id === addOnId)) {
        guests.push(index + 1);
      }
    });
    return guests;
  };

  const resetEditState = () => {
    setRemovedItems(new Set());
    setRemovedGuests(new Set());
  };

  const removeAddOnCompletely = (addOnId: string) => {
    const newRemovedItems = new Set(removedItems);
    editAddOnsPerGuest.forEach((guestAddOns, guestIndex) => {
      // Skip guests that are already removed
      if (removedGuests.has(guestIndex)) return;

      // Check if this guest has this addon
      if (guestAddOns.some(addOn => addOn.add_on_id === addOnId)) {
        const key = `${guestIndex}-${addOnId}`;
        newRemovedItems.add(key);
      }
    });
    setRemovedItems(newRemovedItems);
  };

  const restoreAddOnCompletely = (addOnId: string) => {
    const newRemovedItems = new Set(removedItems);
    editAddOnsPerGuest.forEach((guestAddOns, guestIndex) => {
      // Skip guests that are removed
      if (removedGuests.has(guestIndex)) return;

      // Check if this guest has this addon
      if (guestAddOns.some(addOn => addOn.add_on_id === addOnId)) {
        const key = `${guestIndex}-${addOnId}`;
        newRemovedItems.delete(key);
      }
    });
    setRemovedItems(newRemovedItems);
  };

  const isAddOnCompletelyRemoved = (addOnId: string): boolean => {
    // Check if all instances of this addon are marked for removal
    let hasAnyInstance = false;
    let allInstancesRemoved = true;

    editAddOnsPerGuest.forEach((guestAddOns, guestIndex) => {
      // Skip removed guests
      if (removedGuests.has(guestIndex)) return;

      // Check if this guest has this addon
      if (guestAddOns.some(addOn => addOn.add_on_id === addOnId)) {
        hasAnyInstance = true;
        const key = `${guestIndex}-${addOnId}`;
        if (!removedItems.has(key)) {
          allInstancesRemoved = false;
        }
      }
    });

    return hasAnyInstance && allInstancesRemoved;
  };

  // Set quantity for a specific add-on for a specific guest
  const setAddOnQuantityForGuest = (guestIndex: number, addOnId: string, newQuantity: number) => {
    // Don't allow negative quantities
    if (newQuantity < 0) return;

    setEditAddOnsPerGuest(prev => {
      const newState = [...prev];
      if (!newState[guestIndex]) {
        newState[guestIndex] = [];
      }

      const guestAddOns = [...newState[guestIndex]];
      const existingIndex = guestAddOns.findIndex(ao => ao.add_on_id === addOnId);

      if (existingIndex !== -1) {
        // Update existing add-on quantity (keep at 0, don't remove)
        guestAddOns[existingIndex] = {
          ...guestAddOns[existingIndex],
          quantity: newQuantity
        };
      }

      newState[guestIndex] = guestAddOns;
      return newState;
    });
  };

  return {
    isEditing,
    editGuestCount,
    editAddOnsPerGuest,
    removedItems,
    removedGuests,
    startEdit,
    cancelEdit,
    setEditGuestCount,
    setEditAddOnsPerGuest,
    removeGuest,
    removeAddOnFromGuest,
    restoreGuest,
    restoreAddOnFromGuest,
    isItemMarkedForRemoval,
    getEditModeGuestsWithAddOn,
    getRemovedGuestsWithAddOn,
    resetEditState,
    removeAddOnCompletely,
    restoreAddOnCompletely,
    isAddOnCompletelyRemoved,
    setAddOnQuantityForGuest,
  };
}

